import SubScriptionManagerBase from "./subscriptionmanagerbase";
import SubScriptionManagerSSO from "./subscriptionmanagersso";
import { generateError, createError } from "./../../common/utilities/utils";

import {
  AUTHENTICATED,
  AUTHORIZED,
} from "./../../common/analytics/analyticsevents";
import paymentMethods from "./paymentMethods";
import { requestDownload } from "./subscriptionUtilities";

// this class will implement al the necessary API communication
// and cookie management to authenticate users and authorize
// downloads. It does not implement any UI elements.
export default class SubScriptionManagerSubscription extends SubScriptionManagerBase {
  // Returns the current authentication state.
  get isAuthenticated() {
    // read subscrionCookie  //TODO: why read subscrionCookie? if there is a cookie this._authenticated is true?
    return this._authenticated;
  }

  set isAuthenticated(authenticated) {
    this._authenticated = authenticated;
  }

  // returns a promise that will either reject with an error code when authentication
  // fails, either resolves to success
  // in any case it will also trigger the authenticationstatuschanged event.
  authenticateByUserNamePassword(username, password, downloadToken = "") {
    const authenticationPromise = new Promise((resolve, reject) => {
      // check if there is an SSO cookie if the settings are SSO enabled,
      // else check for the subscription cookie.
      this._sessionManager
        .getSessionInfo()
        .then((sessionId) => {
          this._apiManager
            .validateSubscription(
              sessionId,
              downloadToken,
              username,
              password,
              this._options.subscriptionOptions.subscriptionProvider,
              "1.0.0.0",
              this._sessionManager.userId,
              this._options.subscriptionOptions.authenticationProvider
            )
            .then((data) => {
              if (data.Status !== "Succes") {
                this.removeAuthenticationCookies();
                data.call = "validateSubscription";
                data.errorType = "authentication";
                let error = generateError(data);
                reject(error);
              } else {
                data.username = username;
                this._authenticatedSucces(data);
                this._appState.triggerEvent(AUTHENTICATED);
                resolve();
              }
            })
            .catch((data) => {
              reject(
                createError("VS-ERR", "ValidateSubscription failed", data)
              );
            });
        })
        .catch((data) => {
          this.removeAuthenticationCookies();
          reject(data);
        });
    });
    return authenticationPromise;
  }

  _authenticatedSucces(data) {
    this._authenticated = true;
    // store subscriptionCookie
    this._createSubscriptionCookie(
      data.SubscriptionID,
      data.SubscriptionReference,
      data.SubscriptionUserReference,
      data.SubscriptionType,
      data.username
    );
    this.trigger("subscriptionmanager:authenticationstatuschanged");
    if (this.sso) {
      this.sso.redirect();
    }
  }

  // returns a promise that will either reject with an error code when authentication
  // fails, either resolves to success. This is to be used in case of SSO, or
  // to check if you are autenticated
  // in any case it will also trigger the authenticationstatuschanged event if
  // the current status changed.
  // returns subsID
  authenticate(downloadToken) {
    const authenticationPromise = new Promise((resolve, reject) => {
      // first check for new SSO cookie, using the SSOManager if options SSO enabled set
      if (this._options.SSOEnabled) {
        this.sso = new SubScriptionManagerSSO(
          this._appState,
          this._sessionManager,
          this._apiManager,
          this._cookieManager,
          this._options
        );
        this.sso
          .authenticate(downloadToken)
          .then((data) => {
            if (data.Status !== "success" || data.Status !== "Success") {
              //TODO: type SSO
              this._authenticateCookie(resolve, reject);
            } else {
              this._authenticatedSucces(data);
              resolve();
            } // eslint-disable-next-line
          })
          .catch(() => {
            //failed for some reason -> try normal way
            this._authenticateCookie(resolve, reject);
          });
      } else {
        // else check for subscription cookie
        this._authenticateCookie(resolve, reject);
      }
    });
    return authenticationPromise;
  }

  _authenticateCookie(resolve, reject) {
    //check for subscriptionCookie:
    const cookie = this._cookieManager.getCookie(
      this._cookieManager.cookieKeys.LOGIN
    );
    if (cookie) {
      this._setLoginInformationFromCookie();
      //TODO: check if still good subscription? using hash
      this._authenticated = true;
      this.trigger("subscriptionmanager:authenticationstatuschanged");
      resolve(JSON.parse(cookie).subsId);
    } else {
      this.removeAuthenticationCookies();
      //no cookie  //TODO: show error?
      reject();
    }
  }

  authorize(
    publicationData = {
      contentPackageId: -1,
      publicationId: -1,
      price: -1,
    },
    downloadToken,
    confirmDownload
  ) {
    const promise = new Promise((resolve, reject) => {
      this.authenticate(downloadToken)
        .then((subsId) => {
          this._requestAuthorisation(publicationData, subsId, confirmDownload)
            .then(() => {
              this._appState.triggerEvent(AUTHORIZED);
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
    return promise;
  }

  _requestAuthorisation(publicationData, subsId, confirmDownload) {
    if (publicationData && publicationData.contentPackageId) {
      return this._sessionManager
        .getSessionInfo()
        .then((sessionId) => {
          return this._apiManager
            .requestOrderPost(
              this._sessionManager.userId,
              sessionId,
              publicationData.contentPackageId,
              "1.0.0.0",
              paymentMethods.SUBSCRIPTION
            )
            .then((data) => {
              if (typeof data.StatusMessage !== "string") {
                const error = createError(
                  "RO-SM",
                  "RequestOrder without StatusMessage",
                  data
                );
                return Promise.reject(error);
              } else if (data.Status !== "Succes") {
                const error = createError(
                  "RO-KO",
                  "RequestOrder returned an error",
                  data
                );
                return Promise.reject(error);
              }
              return requestDownload({
                sessionManager: this._sessionManager,
                paymentMethod: paymentMethods.SUBSCRIPTION,
                contentPackageId: publicationData.contentPackageId,
                subsId,
                orderId: data.OrderID,
                confirmDownload,
              });
            })
            .catch((data) => {
              if (
                this._appState.isLoggedIn &&
                data.data.errorType == "authentication"
              ) {
                this.removeAuthenticationCookies();
              }
              return Promise.reject(data);
            });
        })
        .catch((data) => {
          return Promise.reject(data);
        });
    }
    const error = createError(
      "AUT-ERR",
      "no publicationData and contentPackageId",
      {}
    );
    return Promise.reject(error);
  }

  getSSOState() {
    return this.sso.ssoState;
  }

  createSSOStateCookie(redirectUrl) {
    this.sso.setSSOState(redirectUrl);
  }

  removeSSOStateCookie() {
    if (this.sso) {
      this.sso.removeSSOStateCookie();
    }
  }
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/subscriptionmanagersubscription.js